import {PayloadAction, createSlice} from '@reduxjs/toolkit'
import {ZadPromoCodeManagement} from '../../../models/zad'
import {ZadApiError} from '../zad/errors'
import {
  createZadPromoCode,
  fetchZadPromoCodes,
  generateZadPromoCode,
  updateZadPromoCodeStatus,
} from './service'
import {FetchZadPromoCodesResponse, ZadPromoCodesState} from './types'

const initialState: ZadPromoCodesState = {
  items: [],
  total: 0,
  totalPages: 0,
  listLoading: false,
  listInitialized: false,
  actionLoading: false,
  generateLoading: false,
  generatedCode: '',
  error: null,
  errorCode: null,
  errorStatus: null,
}

function applyRejected(state: ZadPromoCodesState, action: PayloadAction<any>) {
  const payload = (action.payload || {}) as ZadApiError
  state.error = payload.error || 'Request failed'
  state.errorCode = payload.errorCode || null
  state.errorStatus = payload.status || null
}

function clearError(state: ZadPromoCodesState) {
  state.error = null
  state.errorCode = null
  state.errorStatus = null
}

function upsertItem(state: ZadPromoCodesState, item?: ZadPromoCodeManagement | null) {
  if (!item?._id) return
  const index = state.items.findIndex((row) => row._id === item._id)
  if (index >= 0) state.items[index] = item
  else state.items.unshift(item)
}

const zadPromoCodesSlice = createSlice({
  name: 'zadPromoCodes',
  initialState,
  reducers: {
    clearZadPromoCodesError: (state: ZadPromoCodesState) => {
      clearError(state)
    },
    clearGeneratedPromoCode: (state: ZadPromoCodesState) => {
      state.generatedCode = ''
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchZadPromoCodes.pending, (state) => {
      state.listLoading = true
      clearError(state)
    })
    builder.addCase(fetchZadPromoCodes.rejected, (state, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.listLoading = false
      state.listInitialized = true
    })
    builder.addCase(
      fetchZadPromoCodes.fulfilled,
      (state, action: PayloadAction<FetchZadPromoCodesResponse>) => {
        state.listLoading = false
        state.listInitialized = true
        clearError(state)
        state.items = action.payload.items
        state.total = action.payload.total
        state.totalPages = action.payload.totalPages
      }
    )

    builder.addCase(generateZadPromoCode.pending, (state) => {
      state.generateLoading = true
      clearError(state)
    })
    builder.addCase(generateZadPromoCode.rejected, (state, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.generateLoading = false
    })
    builder.addCase(
      generateZadPromoCode.fulfilled,
      (state, action: PayloadAction<{success: true; code: string}>) => {
        state.generateLoading = false
        clearError(state)
        state.generatedCode = action.payload.code
      }
    )

    const actionPending = (state: ZadPromoCodesState) => {
      state.actionLoading = true
      clearError(state)
    }
    const actionRejected = (state: ZadPromoCodesState, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.actionLoading = false
    }
    const actionFulfilled = (
      state: ZadPromoCodesState,
      action: PayloadAction<{item: ZadPromoCodeManagement | null}>
    ) => {
      state.actionLoading = false
      clearError(state)
      upsertItem(state, action.payload.item)
    }

    ;[createZadPromoCode, updateZadPromoCodeStatus].forEach((thunk) => {
      builder.addCase(thunk.pending, actionPending)
      builder.addCase(thunk.rejected, actionRejected)
      builder.addCase(thunk.fulfilled, actionFulfilled)
    })
  },
})

export default zadPromoCodesSlice.reducer
export const actions = zadPromoCodesSlice.actions
export {initialState as zadPromoCodesInitialState}
